import { useMemo } from "react";
import {
  determineCurrentTimeZone,
  getIsDaylightSavings,
  ONE_MINUTE_OF_MS,
  ONE_HOUR_OF_MS,
} from "utils/timeUtils";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { timeZonesWikiData } from "utils/timeZonesWikiData";

/** displayable metadata about a date like day-start offset & timezone override setting */
export function useDateMetadataDisplay() {
  const { dayStartMsOffset, timeZoneLocaleString } = useSettings();
  const minute = Math.floor((dayStartMsOffset / ONE_MINUTE_OF_MS) % 60);
  const adjustedHour = Math.floor(
    (dayStartMsOffset - minute * ONE_MINUTE_OF_MS) / ONE_HOUR_OF_MS
  );
  const adjustedMinute = Math.abs(
    Math.floor((dayStartMsOffset / ONE_MINUTE_OF_MS) % 60)
  );
  const isPositive =
    (adjustedHour > 0 && minute >= 0) || (adjustedHour === 0 && minute > 0);
  const isNegative = adjustedHour === 0 && minute < 0;
  const dayStartMsOffsetDisplay =
    dayStartMsOffset === 0
      ? ""
      : `${isPositive ? "+" : isNegative ? "-" : ""}${adjustedHour}:${String(adjustedMinute).padStart(2, "0")}`;
  const { timeZoneUTCString, isInverted } = useMemo(() => {
    const t = timeZonesWikiData.find(
      tz => tz["TZ database name"] === timeZoneLocaleString
    );
    const inverted = t?.Notes?.includes("Sign is intentionally inverted.");
    const utcOffset =
      t?.[
        getIsDaylightSavings() ? "UTC DST offset ±hh:mm" : "UTC offset ±hh:mm"
      ];
    return {
      timeZoneUTCString: utcOffset ? `UTC${utcOffset}` : "",
      isInverted: inverted,
    };
  }, [timeZoneLocaleString]);
  const currentTimezone = determineCurrentTimeZone();

  const timeZoneLocaleStringUninverted = !isInverted
    ? timeZoneLocaleString
    : timeZoneLocaleString.includes("+")
      ? timeZoneLocaleString.replace("+", "-")
      : timeZoneLocaleString.includes("-")
        ? timeZoneLocaleString.replace("-", "+")
        : timeZoneLocaleString;
  const timezoneDisplay =
    timeZoneLocaleString === currentTimezone.localeString
      ? ""
      : `${timeZoneLocaleStringUninverted} (${timeZoneUTCString})`;
  return { dayStartMsOffsetDisplay, timezoneDisplay };
}
